// src/components/auth/sign-up-form.tsx
"use client";

import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import zxcvbn from "zxcvbn";
import countriesLib from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import { getCountryCallingCode, getCountries } from "libphonenumber-js";
import ReactCountryFlag from "react-country-flag";
import Link from "next/link";
import { AtSign, Send } from "lucide-react";
import { cn } from "@/lib/utils";
import { authClient } from "@/lib/auth-client";
import { Form, FormField } from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AuthTextField } from "@/components/molecules/auth/auth-text-field";
import { AuthPasswordField } from "@/components/molecules/auth/auth-password-field";
import { AuthSubmitButton } from "@/components/atoms/auth/auth-submit-button";
import toast from "react-hot-toast";
import { capture } from "@/lib/posthog/client";
import { EVENTS } from "@/lib/posthog/events";
import {
  SIGNUP_VALIDATION_FAILURE,
  signupProperties,
  signupRequestFailureReason,
} from "@/lib/posthog/signup-events";
import { formErrorsFromTree } from "@/lib/posthog/friction";

// Register English locale for country names
countriesLib.registerLocale(enLocale);

const countries = countriesLib.getNames("en");
const supportedCountryCodes = getCountries();
const countryOptions = supportedCountryCodes
  .map((code) => {
    const name = countries[code];
    if (!name) return null;
    return {
      value: code,
      label: name,
      dialingCode: `+${getCountryCallingCode(code)}`,
    };
  })
  .filter(Boolean)
  .sort((a, b) => a.label.localeCompare(b.label)) as {
    value: string;
    label: string;
    dialingCode: string;
  }[];

const formSchema = z
  .object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.string().email("Invalid email address"),
    country: z.string().refine((val) => supportedCountryCodes.includes(val), {
      message: "Please select a valid country",
    }),
    phone: z
      .string()
      .min(5, "Phone number is too short")
      .max(15, "Phone number is too long"),
    telegramHandle: z
      .string()
      .trim()
      .max(64, "Telegram handle is too long")
      .regex(/^(@?[A-Za-z0-9_]{5,32})?$/, "Invalid Telegram handle"),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .refine((val) => zxcvbn(val).score >= 3, {
        message: "Password is too weak",
      }),
    confirmPassword: z.string(),
    minWithdrawFiat: z.number().min(0, "Must be ≥ 0"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

const STRENGTH_LABELS = ["Very Weak", "Weak", "Fair", "Strong", "Very Strong"] as const;

export function SignUpForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"form">) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      country: "",
      phone: "",
      telegramHandle: "",
      password: "",
      confirmPassword: "",
      minWithdrawFiat: 300,
    },
  });

  const {
    handleSubmit,
    control,
    register,
    formState: { errors, isSubmitting },
  } = form;

  const [passwordStrength, setPasswordStrength] = React.useState(0);
  const [selectedCountry, setSelectedCountry] = React.useState<string | null>(null);

  // `signup_start` fires once per mount, `signup_email_entered` once
  // per mount on the email field's first blur with a non-empty value. Both
  // guarded by a ref rather than state, since neither should ever cause a
  // re-render.
  const hasCapturedStart = React.useRef(false);
  const hasCapturedEmailEntered = React.useRef(false);

  React.useEffect(() => {
    if (hasCapturedStart.current) return;
    hasCapturedStart.current = true;
    capture(EVENTS.SIGNUP_START, signupProperties({ country: form.getValues("country") }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emailField = register("email");
  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    emailField.onBlur(e);
    if (hasCapturedEmailEntered.current) return;
    if (!e.target.value.trim()) return;
    hasCapturedEmailEntered.current = true;
    capture(EVENTS.SIGNUP_EMAIL_ENTERED, signupProperties({ country: form.getValues("country") }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    form.setValue("password", newPassword);
    setPasswordStrength(newPassword ? zxcvbn(newPassword).score : 0);
  };

  const handleCountryChange = (countryCode: string) => {
    setSelectedCountry(countryCode);
    form.setValue("country", countryCode, { shouldValidate: true });
  };

  const dialingCode = selectedCountry
    ? countryOptions.find((o) => o.value === selectedCountry)?.dialingCode ?? ""
    : "";

  async function onSubmit(values: z.infer<typeof formSchema>) {
    capture(EVENTS.SIGNUP_SUBMIT, signupProperties({ country: values.country }));
    try {
      // No "is this email taken?" pre-check: it was an unauthenticated oracle
      // over the whole user table. Sign-up itself reports an existing account,
      // behind Better Auth's own rate limit on /sign-up/email.
      const fullName = `${values.firstName} ${values.lastName}`;
      const fullPhone = `${dialingCode}${values.phone}`;
      const telegramHandleTrimmed = values.telegramHandle.trim();

      const { error } = await authClient.signUp.email({
        email: values.email,
        password: values.password,
        name: fullName,
        phone: fullPhone,
        country: values.country,
        telegramHandle: telegramHandleTrimmed ? telegramHandleTrimmed : undefined,
        minWithdrawFiat: values.minWithdrawFiat,
      });

      if (error) {
        console.error("Sign-up error:", error);
        capture(EVENTS.SIGNUP_FAILED, {
          ...signupProperties({ country: values.country }),
          ...signupRequestFailureReason(error),
        });
        toast.error(error.message || "Sign-up failed");
      } else {
        // Issued before the hard navigation below, with sendBeacon so it
        // survives the page unload.
        capture(
          EVENTS.SIGNUP_SUCCESS,
          signupProperties({ country: values.country }),
          { transport: "sendBeacon" },
        );
        toast.success("Sign-up successful! Check your email to verify.");
        localStorage.setItem("signup_email", values.email);
        window.location.href = "/verify-email";
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      capture(EVENTS.SIGNUP_FAILED, {
        ...signupProperties({ country: values.country }),
        ...signupRequestFailureReason(err),
      });
      toast.error("An unexpected error occurred");
    }
  }

  function onInvalid(errors: Record<string, unknown>) {
    capture(EVENTS.SIGNUP_FAILED, {
      ...signupProperties({ country: form.getValues("country") }),
      ...SIGNUP_VALIDATION_FAILURE,
    });
    // One form_error per invalid field, alongside the aggregate
    // signup_failed above.
    for (const formError of formErrorsFromTree("signup", errors)) {
      capture(EVENTS.FORM_ERROR, formError);
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit(onSubmit, onInvalid)}
        className={cn("flex w-full flex-col gap-8", className)}
        noValidate
        {...props}
      >
        <div className="flex flex-col gap-3">
          <h1 className="font-display text-4xl font-bold tracking-tight text-brand-ink">
            Create your account
          </h1>
          <p className="text-base text-brand-ink/70">
            Enter your details below to start accepting payments
          </p>
        </div>

        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <AuthTextField
              id="firstName"
              label="First name"
              autoComplete="given-name"
              error={errors.firstName?.message}
              {...register("firstName")}
            />
            <AuthTextField
              id="lastName"
              label="Last name"
              autoComplete="family-name"
              error={errors.lastName?.message}
              {...register("lastName")}
            />
          </div>

          <AuthTextField
            id="email"
            label="Email address"
            type="email"
            autoComplete="email"
            icon={<AtSign className="size-5" />}
            error={errors.email?.message}
            {...emailField}
            onBlur={handleEmailBlur}
          />

          <FormField
            control={control}
            name="country"
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="country" className="text-sm font-semibold text-brand-ink/70">
                  Country
                </label>
                <Select onValueChange={handleCountryChange} defaultValue={field.value}>
                  <SelectTrigger
                    id="country"
                    className="min-h-14 w-full rounded-xl border-brand-border bg-white px-4 text-base text-brand-ink data-[placeholder]:text-brand-ink/60 focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/30"
                  >
                    <SelectValue placeholder="Select a country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countryOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <ReactCountryFlag countryCode={option.value} svg />
                          {option.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldState.error && (
                  <p role="alert" className="text-sm text-destructive">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />

          <div className="grid grid-cols-[120px_1fr] gap-4">
            <AuthTextField
              id="dialingCode"
              label="Code"
              value={dialingCode}
              readOnly
              tabIndex={-1}
            />
            <AuthTextField
              id="phone"
              label="Phone number"
              type="tel"
              autoComplete="tel-national"
              error={errors.phone?.message}
              {...register("phone")}
            />
          </div>

          <AuthTextField
            id="telegramHandle"
            label="Telegram handle (optional)"
            icon={<Send className="size-4" />}
            error={errors.telegramHandle?.message}
            {...register("telegramHandle")}
          />

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <AuthPasswordField
              id="password"
              label="Password"
              autoComplete="new-password"
              error={errors.password?.message}
              hint={
                form.watch("password")
                  ? `Strength: ${STRENGTH_LABELS[passwordStrength]}`
                  : undefined
              }
              {...register("password")}
              onChange={handlePasswordChange}
            />
            <AuthPasswordField
              id="confirmPassword"
              label="Confirm password"
              autoComplete="new-password"
              error={errors.confirmPassword?.message}
              {...register("confirmPassword")}
            />
          </div>

          <AuthSubmitButton loading={isSubmitting} className="w-full sm:w-auto sm:self-start sm:px-12">
            {isSubmitting ? "Signing up…" : "Sign Up"}
          </AuthSubmitButton>
        </div>

        <p className="text-sm text-brand-ink/70">
          Already have an account?{" "}
          <Link href="/login" className="font-semibold text-brand hover:underline">
            Log in
          </Link>
        </p>
      </form>
    </Form>
  );
}
